import {useCustomQuery} from "../../../query/queryClient";
import {queryHttp} from "../../../query/queryHttp";
import {supplierEntityLogoUrl} from "../services/supplier360Urls";

/**
 * Unchanged domains are refreshed by `get_logo_from_brandfetch` at most nightly, so a registry row
 * that scrolls out of view and back does not re-request its image. A domain edit resolves the logo
 * before the edit response (§2.5) and must reset this query — `staleTime` would keep the old bytes.
 */
const LOGO_STALE_TIME_MS = 60 * 60 * 1000;

/**
 * A data URL rather than an object URL: react-query caches the value across rows and remounts, and
 * there is no cache-eviction hook to revoke an object URL from once it does.
 */
function blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(reader.error ?? new Error('Could not read the logo bytes'));
        reader.onload = () => resolve(String(reader.result));
        reader.readAsDataURL(blob);
    });
}

/**
 * One supplier's logo as a data URL, ready for an `<img src>` (`GET supplier_360/entity/{id}/logo/`).
 *
 * The endpoint needs the app's `Authorization` header, so the browser cannot fetch it from the
 * `src` attribute directly — the bytes come through the same authenticated client as every other
 * call and are inlined instead.
 *
 * Pass `enabled: false` (i.e. `logo_url === ''`) for an entity with no logo on file: that spares a
 * request per row that could only ever 404. A failure is not surfaced — the caller falls back to
 * initials — so this deliberately neither retries nor shows the global error notification.
 */
export function useSupplierLogoQuery(entityId: number | undefined, enabled = true) {
    const url = entityId !== undefined ? supplierEntityLogoUrl(entityId) : '';
    return useCustomQuery({
        queryKey: [url] as const,
        queryFn: async () => blobToDataUrl(
            (await queryHttp.get<Blob>(url, {responseType: 'blob'})).data,
        ),
        enabled: enabled && Boolean(url),
        staleTime: LOGO_STALE_TIME_MS,
        gcTime: LOGO_STALE_TIME_MS,
        retry: false,
    });
}
